// Build 96.6 — Papperskorg (Soft-Delete-Trash) för admin.
// Listar alla poster där `status="raderad"` (eller `deleted_at` är satt) över
// de 7 stödda collections. Restore-knappen sätter tillbaka `deleted_prev_status`.
// Inga hård-raderingar i denna build. Inga R2-filer rörs.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RotateCcw, Trash2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

// Visningsetiketter per källa (samma som i Sammelvyn).
const SOURCE_LABEL = {
  karttipps: "Karttipp",
  foretag: "Företag",
  forening: "Förening",
  vardagskort: "Vardagskort",
  evenemang: "Evenemang",
  anslag: "Anslag",
  bilder: "Foto/Media",
};

// Vilket fält bär stabila numret per collection (kan saknas i äldre poster).
const NR_FIELD = {
  karttipps: "karttipp_nr",
  foretag: "foretag_nr",
  forening: "forening_nr",
  vardagskort: "vardagskort_nr",
};

// Vilket fält bär det visningsbara namnet/titeln per collection.
function getName(row) {
  return (
    row.namn ||
    row.foretagsnamn ||
    row.rubrik ||
    row.titel ||
    "(utan namn)"
  );
}

function getNr(row) {
  const f = NR_FIELD[row.__source];
  return f ? row[f] : null;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const SOURCES = [
  { key: "all", label: "Alla källor" },
  { key: "karttipps", label: "Karttipps" },
  { key: "foretag", label: "Företag" },
  { key: "forening", label: "Föreningar" },
  { key: "vardagskort", label: "Vardagskort" },
  { key: "evenemang", label: "Evenemang" },
  { key: "anslag", label: "Anslag" },
  { key: "bilder", label: "Foto/Media" },
];

export default function PapperskorgPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [srcFilter, setSrcFilter] = useState("all");
  const [restoring, setRestoring] = useState(null); // key: source-id

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/mod/papperskorg`, { headers: modHeaders() });
      setRows(data?.rows || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Kunde inte hämta papperskorgen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const list = srcFilter === "all" ? rows : rows.filter((r) => r.__source === srcFilter);
    // Senast raderade först
    return [...list].sort((a, b) => {
      const da = a.deleted_at || "";
      const db = b.deleted_at || "";
      return db.localeCompare(da);
    });
  }, [rows, srcFilter]);

  const counts = useMemo(() => {
    const c = { total: rows.length };
    for (const r of rows) c[r.__source] = (c[r.__source] || 0) + 1;
    return c;
  }, [rows]);

  const restore = async (row) => {
    const name = getName(row);
    const nr = getNr(row);
    const label = SOURCE_LABEL[row.__source] || row.__source;
    const idStr = nr ? `#${nr}` : `#${String(row.id || "").slice(0, 8)}`;
    const msg =
      `Vill du återställa ${label} ${idStr} – ${name}?\n\n` +
      `Posten får tillbaka sitt tidigare status (${row.deleted_prev_status || "Utkast"}) ` +
      `och blir åter synlig i ${label.toLowerCase()}-listan.`;
    if (!window.confirm(msg)) return;
    const key = `${row.__source}-${row.id}`;
    setRestoring(key);
    try {
      const r = await axios.post(
        `${API}/mod/platser/${row.__source}/${row.id}/restore`,
        {},
        { headers: modHeaders() }
      );
      // Optimistisk lokal update — ta bort från listan direkt
      setRows((prev) => prev.filter((x) => !(x.__source === row.__source && x.id === row.id)));
      toast.success("Återställd", {
        description: `${label} ${idStr} – status: ${r.data?.status || "Utkast"}`,
        duration: 3500,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "okänt fel";
      toast.error(`Kunde inte återställa: ${detail}`);
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div data-testid="papperskorg-panel" className="bg-white border border-warm-border rounded-lg shadow-sm">
      <div className="px-4 py-3 border-b border-warm-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-base font-semibold text-ink flex items-center gap-2">
            <Trash2 size={16} className="text-falu" /> Papperskorg – raderade poster
          </h3>
          <p className="text-xs text-bark/70 mt-0.5">
            Soft-deleted poster över alla 7 collections. Återställning sätter tillbaka det ursprungliga statuset. Inga hård-raderingar i denna vy. Bilder/Foto-filer i R2 är orörda.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="papperskorg-refresh"
          className="text-xs px-2.5 py-1.5 border border-warm-border rounded-sm hover:border-pine/50 text-bark hover:text-ink"
        >
          Uppdatera
        </button>
      </div>

      {/* Filter-rad + räkning */}
      <div className="px-4 py-2.5 border-b border-warm-border bg-bone-alt/30 flex flex-wrap items-center gap-2 text-xs">
        <span data-testid="papperskorg-total" className="inline-flex items-center gap-1 bg-parchment border border-warm-border rounded-full px-2.5 py-1">
          <strong>{counts.total}</strong><span>totalt</span>
        </span>
        {SOURCES.filter((s) => s.key !== "all").map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1 bg-parchment border border-warm-border rounded-full px-2.5 py-1 text-bark">
            <strong>{counts[s.key] || 0}</strong><span>{s.label.toLowerCase()}</span>
          </span>
        ))}
        <span className="ml-auto">
          <label className="inline-flex items-center gap-2">
            <span className="text-bark/65">Filtrera:</span>
            <select
              value={srcFilter}
              onChange={(e) => setSrcFilter(e.target.value)}
              data-testid="papperskorg-src-filter"
              className="text-xs px-2 py-1 border border-warm-border rounded-sm bg-white"
            >
              {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </span>
      </div>

      {/* Innehåll */}
      {err && (
        <div className="px-4 py-3 text-sm text-falu flex items-center gap-2">
          <AlertCircle size={14} /> {err}
        </div>
      )}
      {!err && loading && (
        <p className="px-4 py-6 text-sm text-bark/60 text-center">Laddar…</p>
      )}
      {!err && !loading && filtered.length === 0 && (
        <p data-testid="papperskorg-empty" className="px-4 py-8 text-sm text-bark/60 text-center">
          Papperskorgen är tom — inga raderade poster för aktuellt filter.
        </p>
      )}
      {!err && !loading && filtered.length > 0 && (
        <ul className="divide-y divide-warm-border max-h-[640px] overflow-y-auto" data-testid="papperskorg-list">
          {filtered.map((r) => {
            const key = `${r.__source}-${r.id}`;
            const label = SOURCE_LABEL[r.__source] || r.__source;
            const nr = getNr(r);
            const idStr = nr ? `#${nr}` : `#${String(r.id || "").slice(0, 8)}`;
            return (
              <li
                key={key}
                data-testid={`papperskorg-row-${r.__source}-${r.id}`}
                className="px-4 py-3 flex items-start gap-3 hover:bg-bone-alt/40"
              >
                <span className="shrink-0 inline-flex items-center text-[11px] font-mono font-semibold text-bark bg-parchment border border-warm-border rounded px-1.5 py-0.5 mt-0.5">
                  {label} {idStr}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink leading-tight truncate">
                    {getName(r)}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-bark/75 mt-0.5">
                    <span>
                      Tidigare status: <strong>{r.deleted_prev_status || "—"}</strong>
                    </span>
                    <span>Raderad: {fmtDateTime(r.deleted_at)}</span>
                    <span>Av: {r.deleted_by || "—"}</span>
                  </div>
                  {r.deleted_reason && (
                    <p className="text-[11px] text-bark/70 mt-1 italic">
                      Orsak: {r.deleted_reason}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => restore(r)}
                  disabled={restoring === key}
                  data-testid={`papperskorg-restore-${r.__source}-${r.id}`}
                  title="Återställ – sätter tillbaka tidigare status"
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-pine border border-pine/40 hover:bg-pine/10 rounded transition-colors disabled:opacity-50"
                >
                  <RotateCcw size={13} /> Återställ
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
